/**
 * The shape of a concern's two-way conversation, and the few rules the screens
 * need to read it.
 *
 * Shared by ComplaintsScreen and ComplaintsScreen.web so the native and web
 * builds cannot end up disagreeing about whether a concern is finished - which
 * is exactly the failure this whole flow exists to prevent.
 */

/** PARENT is the family; TEACHER is the school - a class teacher or an admin. */
export type ComplaintSide = 'PARENT' | 'TEACHER';

export interface ComplaintThreadMessage {
  id: string;
  senderRole: ComplaintSide;
  senderName?: string | null;
  message: string;
  createdDate: string;
  /** Set by the server, so the screen never matches ids to know whose bubble this is. */
  isMine: boolean;
}

export interface ComplaintThread {
  id: string;
  complaintNumber: string;
  /** Which side RAISED it. */
  type: ComplaintSide;
  description: string;
  /** PENDING | AWAITING_PARENT | AWAITING_SCHOOL | RESOLVED */
  complaintStatus: string;
  createdDate: string;
  raisedByName?: string | null;

  isSchoolResolved: boolean;
  schoolResolvedDate?: string | null;
  schoolResolvedByName?: string | null;

  isParentResolved: boolean;
  parentResolvedDate?: string | null;
  parentResolvedByName?: string | null;

  mySide: ComplaintSide;
  hasMyResolution: boolean;
  canResolve: boolean;
  isClosed: boolean;

  messages: ComplaintThreadMessage[];
}

/** Tolerates both casings, same convention as the mappers in this app. */
export function mapComplaintThread(raw: any): ComplaintThread {
  const messages = raw?.messages ?? raw?.Messages ?? [];

  return {
    id: raw?.id ?? raw?.Id ?? '',
    complaintNumber: raw?.complaintNumber ?? raw?.ComplaintNumber ?? '',
    type: (raw?.type ?? raw?.Type ?? 'TEACHER') as ComplaintSide,
    description: raw?.description ?? raw?.Description ?? '',
    complaintStatus: raw?.complaintStatus ?? raw?.ComplaintStatus ?? 'PENDING',
    createdDate: raw?.createdDate ?? raw?.CreatedDate ?? new Date().toISOString(),
    raisedByName: raw?.raisedByName ?? raw?.RaisedByName ?? null,

    isSchoolResolved: raw?.isSchoolResolved ?? raw?.IsSchoolResolved ?? false,
    schoolResolvedDate: raw?.schoolResolvedDate ?? raw?.SchoolResolvedDate ?? null,
    schoolResolvedByName: raw?.schoolResolvedByName ?? raw?.SchoolResolvedByName ?? null,

    isParentResolved: raw?.isParentResolved ?? raw?.IsParentResolved ?? false,
    parentResolvedDate: raw?.parentResolvedDate ?? raw?.ParentResolvedDate ?? null,
    parentResolvedByName: raw?.parentResolvedByName ?? raw?.ParentResolvedByName ?? null,

    mySide: (raw?.mySide ?? raw?.MySide ?? 'PARENT') as ComplaintSide,
    hasMyResolution: raw?.hasMyResolution ?? raw?.HasMyResolution ?? false,
    canResolve: raw?.canResolve ?? raw?.CanResolve ?? false,
    isClosed: raw?.isClosed ?? raw?.IsClosed ?? false,

    messages: messages.map((m: any) => ({
      id: m?.id ?? m?.Id ?? '',
      senderRole: (m?.senderRole ?? m?.SenderRole ?? 'TEACHER') as ComplaintSide,
      senderName: m?.senderName ?? m?.SenderName ?? null,
      message: m?.message ?? m?.Message ?? '',
      createdDate: m?.createdDate ?? m?.CreatedDate ?? '',
      isMine: m?.isMine ?? m?.IsMine ?? false,
    })),
  };
}

/**
 * What a status should say to THIS reader. The app is always the family's side,
 * so "AWAITING_PARENT" is a request of the person holding the phone, while
 * "AWAITING_SCHOOL" is a wait on somebody else - the raw value says neither.
 */
export function complaintStatusLabel(status: string | undefined): string {
  switch ((status || '').toUpperCase()) {
    case 'RESOLVED':
      return 'Resolved';
    case 'AWAITING_PARENT':
      return 'Awaiting your confirmation';
    // Legacy rows from the old one-click acknowledgement; the server reports these
    // as AWAITING_SCHOOL, so this is only a fallback.
    case 'ACKNOWLEDGED':
    case 'AWAITING_SCHOOL':
      return 'Awaiting school';
    default:
      return 'Pending';
  }
}

/** Badge colours, keyed off the same statuses. */
export function complaintStatusColors(status: string | undefined): { bg: string; text: string } {
  switch ((status || '').toUpperCase()) {
    case 'RESOLVED':
      return { bg: '#D1FAE5', text: '#059669' };
    case 'AWAITING_PARENT':
      return { bg: '#FEF3C7', text: '#B45309' };
    case 'ACKNOWLEDGED':
    case 'AWAITING_SCHOOL':
      return { bg: '#FFEDD5', text: '#C2410C' };
    default:
      return { bg: '#FEE2E2', text: '#DC2626' };
  }
}

/** The one-line explanation of whose move it is, from the family's side. */
export function complaintSignOffHint(thread: ComplaintThread): string {
  if (thread.isClosed)
    return 'Both sides have confirmed this is resolved. It is now closed.';

  if (thread.hasMyResolution)
    return 'You have confirmed this is resolved. It stays open until the school confirms too.';

  if (thread.isSchoolResolved)
    return 'The school has confirmed this is resolved. It closes once you confirm too.';

  return 'This concern closes only when both sides confirm it is resolved.';
}
